import { ref, watch, type Ref } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

// Most-recently-used navigation history, persisted to localStorage so it survives
// across sessions (unlike sessionPersist which is per-tab). Module-level reactive
// singleton (same pattern as lib/realtime.ts) so the AppShell toolbar button, the
// app-root drawer, and the router afterEach hook all share one source of truth and the
// open/close state + list survive AppShell's per-route remount.
//
// MRU semantics: navigating to a path already in the list removes the duplicate and
// re-inserts it at the top, so the newest is always entries[0] and "go back" is just
// clicking the second item. Capped at 30 distinct views.

const KEY = 'nav-history.v1';
const OPEN_KEY = 'nav-history-open.v1';
const CAP = 30;

export interface NavEntry {
  // route.fullPath — the dedupe key and the push() target.
  path: string;
  // Friendly label derived from the route (raw path shown as a subtitle in the UI).
  label: string;
  // Epoch ms of the most recent visit (updated when an existing entry moves to top).
  at: number;
}

function load(): NavEntry[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter(
          (e): e is NavEntry =>
            !!e && typeof e.path === 'string'
            && typeof e.label === 'string' && typeof e.at === 'number',
        )
      : [];
  } catch {
    return [];
  }
}

function persist(list: NavEntry[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // localStorage disabled / quota — best-effort, mirrors sessionPersist.
  }
}

export const navEntries: Ref<NavEntry[]> = ref(load());

// Drawer open state lives here (not in AppShell, which remounts per route) so toggling
// it survives navigation and the drawer can be opened/closed without losing content.
// Persisted to localStorage too: the module re-initialises on a full page load — which
// includes the logout→login round-trip — so a drawer left open is restored on the next
// session / re-login. (App.vue still gates actual display to authenticated routes so a
// restored-open state never splashes over the public landing page.)
function loadOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

export const navDrawerVisible: Ref<boolean> = ref(loadOpen());

watch(navDrawerVisible, (open) => {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // localStorage disabled / quota — best-effort, mirrors the list persistence.
  }
});

export function recordNav(path: string, label: string): void {
  const without = navEntries.value.filter((e) => e.path !== path);
  const next = [{ path, label, at: Date.now() }, ...without].slice(0, CAP);
  navEntries.value = next;
  persist(next);
}

export function clearNavHistory(): void {
  navEntries.value = [];
  persist([]);
}

// Friendly label from a resolved route. Routes are path-only (no `name`s), so derive
// from params with a precedence: service > project > group > static page.
export function labelForRoute(route: RouteLocationNormalized): string {
  const p = route.params as Record<string, string | undefined>;
  const env = p.env ? ` @ ${p.env}` : '';

  if (p.name) return `Service: ${p.name}${env}`;
  if (p.project) return `${p.group} / ${p.project}${env}`;
  if (p.group) return `Group: ${p.group}${env}`;

  switch (route.path) {
    case '/groups': return 'Groups';
    case '/services': return 'Services';
    case '/secrets': return 'Secrets';
    case '/api-keys': return 'API keys';
    case '/environments': return 'Environments';
    case '/audit': return 'Audit';
    case '/profile': return 'Profile';
    default: return route.path;
  }
}
